import styled from "styled-components";

export const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

export const Header = styled.header`
  background: #fff;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  padding: 1rem 0;
  position: sticky;
  top: 0;
  z-index: 100;
`;

export const Nav = styled.nav`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

export const Logo = styled.h1`
  color: #333;
  font-size: 1.8rem;
  margin: 0;
  font-weight: bold;
`;

export const NavLinks = styled.div`
  display: flex;
  align-items: center;
  gap: 2rem;
`;

export const NavLink = styled.a`
  color: #666;
  text-decoration: none;
  font-weight: 500;
  transition: color 0.2s;

  &:hover {
    color: #333;
  }
`;

export const CartButton = styled.button`
  background: #007bff;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  position: relative;
  transition: background-color 0.2s;

  &:hover {
    background: #0056b3;
  }
`;

export const CartCount = styled.span`
  position: absolute;
  top: -8px;
  right: -8px;
  background: #dc3545;
  color: white;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  font-size: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
`;

export const Main = styled.main`
  padding: 2rem 0;
`;

export const ProductGrid = styled.div`
  display: grid;
  /* Denser than a normal shop on purpose: each browser is one tile of a
     ten-window demo wall, so more of the catalogue has to be on screen at
     once. See ProductThumb. */
  grid-template-columns: repeat(auto-fill, minmax(210px, 1fr));
  gap: 1rem;
  margin-top: 1.5rem;
`;

export const ProductCard = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  transition: transform 0.2s, box-shadow 0.2s;
  cursor: pointer;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  }
`;

export const ProductImage = styled.img`
  width: 100%;
  height: 250px;
  object-fit: cover;
`;

/**
 * Locally-rendered product thumbnail: a colour block and a glyph, no network.
 *
 * Replaces the remote unsplash photos for the waiting-room demo. Ten browsers
 * loading eight external images each is 80 off-CDN requests on conference wifi,
 * for no benefit to the story - and the 250px photos meant only one row of
 * products fit a tiled window, so the audience saw a picture instead of someone
 * shopping.
 */
export const ProductThumb = styled.div<{ $hue: number }>`
  width: 100%;
  height: 120px;
  display: grid;
  place-items: center;
  font-size: 2.6rem;
  line-height: 1;
  user-select: none;
  background: linear-gradient(
    135deg,
    hsl(${(p) => p.$hue} 70% 62%),
    hsl(${(p) => (p.$hue + 40) % 360} 68% 48%)
  );
`;

/**
 * Flex column with the button pushed to the bottom, so a one-line and a
 * two-line product name still produce cards of the same shape in a grid row.
 */
export const ProductInfo = styled.div`
  padding: 0.9rem 1rem 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  flex: 1; /* fill the card, so margin-top:auto below can do its job */

  & > button {
    margin-top: auto;
  }
`;

export const ProductName = styled.h3`
  margin: 0 0 0.5rem 0;
  font-size: 1.2rem;
  color: #333;
  line-height: 1.4;
`;

export const ProductPrice = styled.p`
  font-size: 1.4rem;
  font-weight: bold;
  color: #007bff;
  margin: 0.5rem 0;
`;

export const ProductDescription = styled.p`
  color: #666;
  font-size: 0.85rem;
  line-height: 1.45;
  margin: 0.4rem 0 0.8rem 0;
  /* Clamped so a long description cannot push "Add to Cart" out of a tile. */
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

export const Button = styled.button`
  background: #28a745;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  font-size: 0.9rem;
  transition: background-color 0.2s;
  width: 100%;

  &:hover {
    background: #218838;
  }

  &:disabled {
    background: #6c757d;
    cursor: not-allowed;
  }
`;

export const PageTitle = styled.h1`
  color: #333;
  font-size: 2.5rem;
  margin-bottom: 1rem;
  text-align: center;
`;

export const FormContainer = styled.div`
  max-width: 600px;
  margin: 0 auto;
  background: white;
  padding: 2rem;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

export const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

export const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: #333;
`;

export const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
  }
`;

export const CartItem = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  border-bottom: 1px solid #eee;

  &:last-child {
    border-bottom: none;
  }
`;

export const CartItemImage = styled.img`
  width: 80px;
  height: 80px;
  object-fit: cover;
  border-radius: 4px;
`;

export const CartItemInfo = styled.div`
  flex: 1;
`;

export const CartItemName = styled.h4`
  margin: 0 0 0.5rem 0;
  color: #333;
`;

export const CartItemPrice = styled.p`
  margin: 0;
  color: #007bff;
  font-weight: bold;
`;

export const QuantityControls = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

export const QuantityButton = styled.button`
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  width: 32px;
  height: 32px;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s;

  &:hover {
    background: #e9ecef;
  }
`;

export const QuantityDisplay = styled.span`
  min-width: 40px;
  text-align: center;
  font-weight: 500;
`;

export const CartSummary = styled.div`
  background: #f8f9fa;
  padding: 1.5rem;
  border-radius: 8px;
  margin-top: 2rem;
`;

export const CartTotal = styled.div`
  font-size: 1.5rem;
  font-weight: bold;
  color: #333;
  text-align: right;
  margin-bottom: 1rem;
`;

export const EmptyCart = styled.div`
  text-align: center;
  padding: 3rem 2rem;
  color: #666;

  h2 {
    color: #999;
    margin-bottom: 1rem;
  }
`;
